/** Balance, bet, and win tracking */

import { GameConfig } from '../config/GameConfig';
import { Logger } from '../utils/Logger';

const logger = new Logger('BalanceManager');

type BalanceChangeCallback = (data: { balance: number; lastWin: number; totalBet: number }) => void;

/** Manages player balance, bet amount, and win tracking */
export class BalanceManager {
  private _balance: number;
  private _currentBetIndex: number;
  private _totalWin: number = 0;
  private _listeners: Set<BalanceChangeCallback> = new Set();

  constructor(initialBalance: number = GameConfig.STARTING_BALANCE) {
    this._balance = initialBalance;
    // Default bet: 0.05 (index 2 in BET_LEVELS)
    this._currentBetIndex = GameConfig.BET_LEVELS.indexOf(0.05);
    if (this._currentBetIndex < 0) this._currentBetIndex = 2;

    logger.info(
      `BalanceManager initialized: balance=${this._balance}, ` +
      `betLevel=${this.currentBetLevel}, totalBet=${this.totalBet}`
    );
  }

  get balance(): number {
    return this._balance;
  }

  get currentBetLevel(): number {
    return GameConfig.BET_LEVELS[this._currentBetIndex];
  }

  get currentBetIndex(): number {
    return this._currentBetIndex;
  }

  /** Total bet = betLevel * wager multiplier (50) */
  get totalBet(): number {
    return this.currentBetLevel * GameConfig.WAGER_MULTIPLIER;
  }

  get totalWin(): number {
    return this._totalWin;
  }

  canAffordBet(): boolean {
    return this._balance >= this.totalBet;
  }

  /** Deduct the current total bet from balance. Returns the totalBet deducted. */
  deductBet(): number {
    const bet = this.totalBet;
    this._balance -= bet;
    this._totalWin = 0;
    logger.info(`Bet deducted: -${bet.toFixed(2)}, balance=${this._balance.toFixed(2)}`);
    this._notifyListeners();
    return bet;
  }

  /** Deduct a custom amount from balance (e.g. buy feature cost). Returns true if successful. */
  deductAmount(amount: number): boolean {
    if (this._balance < amount) {
      logger.warn(`Cannot deduct ${amount.toFixed(2)}: insufficient balance ${this._balance.toFixed(2)}`);
      return false;
    }
    this._balance -= amount;
    logger.info(`Amount deducted: -${amount.toFixed(2)}, balance=${this._balance.toFixed(2)}`);
    this._notifyListeners();
    return true;
  }

  /** Add a win amount to the balance */
  addWin(amount: number): void {
    this._balance += amount;
    this._totalWin = amount;
    logger.info(`Win added: +${amount.toFixed(4)}, balance=${this._balance.toFixed(2)}`);
    this._notifyListeners();
  }

  /** Set balance to an exact value (used by QA tools) */
  setBalance(amount: number): void {
    this._balance = amount;
    logger.info(`Balance set to ${amount.toFixed(2)}`);
    this._notifyListeners();
  }

  setBetIndex(index: number): void {
    if (index >= 0 && index < GameConfig.BET_LEVELS.length) {
      this._currentBetIndex = index;
      logger.debug(`Bet index set to ${index}, betLevel=${this.currentBetLevel}, totalBet=${this.totalBet}`);
      this._notifyListeners();
    }
  }

  incrementBet(): void {
    this.setBetIndex(this._currentBetIndex + 1);
  }

  decrementBet(): void {
    this.setBetIndex(this._currentBetIndex - 1);
  }

  maxBet(): void {
    this.setBetIndex(GameConfig.BET_LEVELS.length - 1);
  }

  onChange(callback: BalanceChangeCallback): void {
    this._listeners.add(callback);
  }

  offChange(callback: BalanceChangeCallback): void {
    this._listeners.delete(callback);
  }

  private _notifyListeners(): void {
    const data = { balance: this._balance, lastWin: this._totalWin, totalBet: this.totalBet };
    this._listeners.forEach((cb) => cb(data));
  }
}
